/**
 * Defers loading an image until it scrolls near the viewport, swapping
 * `data-src`/`data-srcset` into `src`/`srcset` on intersection instead of
 * relying on the browser's native `loading="lazy"` distance heuristic (which
 * scales with connection speed and can trigger loading several viewport
 * heights early).
 */
class DeferredImage extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const image = this.querySelector('img');
    if (!image || (!image.dataset.src && !image.dataset.srcset)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#load(image);
      },
      { rootMargin: '200px 0px' }
    );

    // Observe the image itself, not this element - it's styled `display:
    // contents` so it has no box of its own for the observer to measure.
    this.#observer.observe(image);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * @param {HTMLImageElement} image
   */
  #load(image) {
    const { src, srcset } = image.dataset;
    if (src) image.src = src;
    if (srcset) image.srcset = srcset;
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');

    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('deferred-image')) {
  customElements.define('deferred-image', DeferredImage);
}
